import { useRef, useState, type ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

import { Arrows } from '@/core/tamagoshiTailwind/components/Arrows/Arrows';
import {
  Scrollable,
  ScrollableHandle,
} from '@/core/tamagoshiTailwind/components/Scrollable/Scrollable';

type PlaylistProps = {
  /** Scrollable items to render within the playlist */
  children: ReactNode[];
  /** Accessible label for the left control */
  controlTitleLeft: string;
  /** Accessible label for the right control */
  controlTitleRight: string;
  /** Whether it should bleed over the padding in mobile viewports */
  fullViewportOverflowMobile?: boolean;
  /** Whether it should bleed over the padding in tablet viewports */
  fullViewportOverflowTablet?: boolean;
  /** Whether it should bleed over the padding in desktop viewports */
  fullViewportOverflowDesktop?: boolean;
  /** Callback when clicking the right button */
  onNext?(): () => void;
  /** Callback when clicking the left button */
  onPrevious?(): () => void;
};

/**
 * Render a Scrollable with the provided items, and Arrows according to Design System.
 * Allows full viewport with margin bleeding on the different breakpoints
 *
 * Assume the provided React nodes already have their unique `key`. It also
 * assumes that the items' height are already normalized and set.
 *
 * See the related [Figma](figma) for more details.
 *
 * [figma]: https://www.figma.com/design/KAiRlBccr6bFYmKby9wQrR?node-id=15914-35802
 */
export const Playlist = ({
  children,
  controlTitleLeft,
  controlTitleRight,
  fullViewportOverflowDesktop,
  fullViewportOverflowMobile,
  fullViewportOverflowTablet,
  onNext,
  onPrevious,
}: PlaylistProps) => {
  const scrollable = useRef<ScrollableHandle>(null);
  /**
   * The max value is the max scrollLeft position, which will be calculated by
   * the scrollable when it will mount. But the initial value is set to "1"
   * (instead of 0), so the right arrow is "enabled" at load time, and so
   * avoiding a blink at startup.
   */
  const [scrollState, setScrollState] = useState({
    index: 0,
    position: 0,
    min: 0,
    max: 1,
  });

  const handleNext = () => {
    scrollable.current?.scrollTowards('right');
    onNext?.();
  };
  const handlePrevious = () => {
    scrollable.current?.scrollTowards('left');
    onPrevious?.();
  };

  // For wide, the overflow padding is computed from max 1440px / 1376px layout
  // 2 * (688px + 32px (desired padding)) = 1440px

  return (
    <div className="flex flex-col items-end gap-s">
      <Scrollable
        className={twJoin(
          'snap-mandatory gap-s *:snap-start max-desktop:snap-x',
          fullViewportOverflowMobile && 'max-tablet:scroll-p-m max-tablet:px-m',
          fullViewportOverflowTablet &&
            'tablet:max-desktop:scroll-p-m tablet:max-desktop:px-m',
          fullViewportOverflowDesktop &&
            'desktop:px-xl wide:px-[calc(50%-688px)]',
        )}
        onScrollChange={setScrollState}
        ref={scrollable}
      >
        {children}
      </Scrollable>
      <Arrows
        disabledLeft={scrollState.position <= scrollState.min}
        disabledRight={scrollState.position >= scrollState.max}
        onLeftClick={handlePrevious}
        onRightClick={handleNext}
        titleLeft={controlTitleLeft}
        titleRight={controlTitleRight}
        className={twJoin(
          'hidden desktop:flex',
          fullViewportOverflowDesktop &&
            'desktop:mr-xl wide:mr-[calc(50%-688px)]',
        )}
      />
    </div>
  );
};
